// import '../adaptivePractice.css';
import { renderedContext, AdaptiveChallengeContext } from '../context/context';
import { useState, useContext, useEffect, useRef } from "react";
import { ReactEvent } from "@mathbuddy/adaptive-practice-events";
import { stylesMap, renderer, IConfig } from "./Render";
import { Player } from "@lottiefiles/react-lottie-player";
import { IReviewMode } from '../features/adaptivePractice/interface/interface';
import { FAExclamationCircle, FAIncorrect, FATickMark } from '../components/atoms/fontawesomeIcon/Fontawesome';

export const CheckAnswerAdaptivePractice = ({ className, id, style, children, clickedVal, actualVal, clickable, extraProps }: IConfig) => {

    // console.log(extraProps);
    const state: any = useContext(renderedContext);
    const challengeContext: any = useContext(AdaptiveChallengeContext);
    let exp = state.explanation;
    let hint = state.hint;
    const reviewMode: IReviewMode = state.reviewMode;
    // const adaptivePracticeReducer: IAdaptivePracticeReducer = useSelector((state: IReducer) => state.adaptivePracticeReducer);

    let [clickCount, setClickCount] = useState(0);
    const [clickedChoice, setClickedChoice] = useState<any>(null);
    /** The same pick, where a handler registered earlier can still read it. */
    const clickedChoiceRef = useRef<any>(null);
    const [showHint, setShowHint] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [isCorrectAns, setIsCorrectAns] = useState(false);
    const [disabled, setDisabled] = useState(reviewMode === undefined ? false : true);
    const [showFinish, setShowFinish] = useState(false);

    // Shown, not played (the quiz builder's cards and its preview). Nothing is marked `disabled`:
    // that greys a question out, and the teacher is looking at it. It simply does not respond -
    // no answer recorded, and no choice marked as chosen. (The `disabled` state is untouched for
    // another reason too: it is what opens the explanation box, which a quiz never shows.)
    const isReadOnly = state?.readOnly === true;

    // Shown to whoever BUILT the question, not to whoever answers it: the teacher asked to see the
    // worked solution, which is the same explanation a student gets after answering. Every question
    // is authored with one; the check is only so a question somehow missing it shows nothing rather
    // than taking the frame down on `exp.extraProps`.
    const isShowAnswer = state?.showAnswer === true && exp !== undefined;

    /**
     * Reading an attempt back rather than making one. `disabled` starts true here, so nothing can be
     * answered, and the box below carries the verdict - right, wrong, or never attempted.
     */
    const isReviewing = reviewMode !== undefined;

    /**
     * ONE attempt instead of two. Set by the adaptive quiz; every other mode leaves it off.
     *
     * This says nothing about what is REVEALED - that is showAnswer's job below. A mode can have one
     * attempt and still explain the answer afterwards, and the quiz happens to want neither.
     */
    const isOneAttempt = Boolean(state.isSingleAttempt);

    /**
     * THE FRAME HAS ASKED FOR THE ANSWER TO BE HELD until the student says they are done with the
     * question - which it does by giving this part a way to hand one in.
     *
     * Asked of the handle itself rather than of a flag beside it: the frame offers registerCommit
     * only on a screen that is handed in (an assessment or a quiz), so having it IS the instruction,
     * and the two can never disagree. Without it nothing below changes - a tap answers, the engine's
     * Done is drawn, and the question behaves as it always has.
     */
    const defersAnswer = typeof state?.registerCommit === 'function';

    /**
     * Nothing is revealed: no hint, no explanation, however they answer. The quiz sets this while a
     * student is playing, because a test must not hand over the answer to a question just missed.
     *
     * Only an explicit `false` means this. A mode that never sets showAnswer leaves it undefined,
     * which means "decide from the answer" - the practice behaviour, unchanged.
     */
    const revealsNothing = state?.showAnswer === false;

    /**
     * THE EXPLANATION BOX - the three unrelated reasons it opens.
     *
     *   isShowAnswer    the teacher asked for the answers, on a question nobody is answering.
     *   reviewing       an attempt is being read back, and the box carries its verdict.
     *   a mistake       they answered wrong, in a mode that explains mistakes.
     *
     * The last one is the only one with conditions on it, and they are both needed: `disabled` means
     * the answer is in and final (so the box does not flash open mid-attempt in practice, where the
     * first wrong answer gets a hint and a second try), and `!revealsNothing` is the mode saying it
     * reveals nothing at all - a test, which must not hand over the answer to a question just
     * missed. Note it is REVEALING that gates this and not the number of attempts: a one-attempt
     * mode that does explain its answers gets the box, which is what showAnswer: undefined means.
     *
     * What the box then CONTAINS is decided separately below - the verdict only in a review, the
     * Next/Finish button only where the host wants one, the explanation itself only while it is
     * unfolded. So the box can be open with the explanation collapsed, which is what the up and down
     * arrows are for.
     */
    const showExplanationBox =
        isShowAnswer ||
        (disabled && isReviewing) ||
        (disabled && !isCorrectAns && !revealsNothing);

    /**
     * Asking for the answers opens the explanation, which is the point of asking.
     *
     * It OPENS it rather than holding it open: the arrows still work, so a teacher who wants the
     * question to themselves can fold the answer away and bring it back. Written as an effect on the
     * flag, not as the initial state, because the cards are already mounted when the teacher presses
     * the button - seeding it at mount would leave them looking at a collapsed box.
     */
    useEffect(() => {
        if (isShowAnswer) setShowExplanation(true);
    }, [isShowAnswer]);

    const markChoice = (id: string) => {
        if (isReadOnly) return;
        state?.markTouched?.();
        clickedChoiceRef.current = id;
        setClickedChoice(id);
    };

    /**
     * Empty the question's input boxes.
     *
     * Said out loud because the box no longer empties itself: it used to be a React element with a
     * value of its own, so ANY re-render wiped it, and an attempt was one. That accident is what gave
     * practice its blank box for a second try - and it is wanted there, so here it is, on purpose and
     * only where it was.
     */
    const clearInputBoxes = () => {
        const event = { id: '', clickedVal: "text-val-check " };
        const boxes = document.getElementsByClassName(event.clickedVal);
        if (boxes.length === 0) return;
        new ReactEvent(event).handleEvent({ classKey: 'inputBoxState', fnName: 'setInputBoxValues' },
            Array.from(boxes, () => ''));
    }

    const onClick = (id: string, clickedVal: any, actualVal: any, extraProps: any) => {
        if (isReadOnly) return;
        state?.markTouched?.();
        setShowHint(false)
        extraProps = { ...extraProps, feedback: state.isSafeZone ? true : clickCount > 0 ? true : false }
        let answerObj = new ReactEvent({ id, clickedVal, actualVal, extraProps }).handleEvent(extraProps.event);
        let count = clickCount + 1;
        /**
         * NOT WHERE NOTHING ON SCREEN WOULD SHOW IT - which is a quiz: one attempt, nothing revealed,
         * and the question left open so the answer can be changed and saved again.
         *
         * `isCorrectAns` is read by the explanation box alone and `clickCount` by the second attempt,
         * so in a quiz both would only re-render the question - and a re-render is not free here. The
         * ENGINE marks the box it is typing into with a class it adds in the DOM, and React, redrawing
         * from the question's config, takes that mark off: focus jumped back to the first box, and the
         * next key went nowhere until the student clicked a box to put the mark back. Saving an answer
         * should leave the question exactly as the student left it.
         */
        if (!(defersAnswer && revealsNothing)) {
            setIsCorrectAns(answerObj.isCorrectAns);
            setClickCount(count);
        }

        /**
         * WHAT HAPPENS TO THE QUESTION NOW - one branch per mode, and they differ in only three
         * things: how many attempts there are, whether a mistake is explained, and whether this
         * question ends the session.
         *
         *                     attempts   explains a mistake   can end the session
         *   daily challenge       1              yes            on the last question
         *   safe zone             1              yes            on the last question
         *   quiz                  1              NO             no - it has its own Submit
         *   everything else       2              on the 2nd     only if the host opts in
         *
         * That last branch is not only adaptive practice. HOTS and the Rise session and assessment
         * all fall through to it, because none of them sets a flag of its own - and for HOTS that is
         * the point rather than an oversight: it is scored 5 for a right answer first time and 3
         * after a hint, which only means anything where there IS a second attempt and a hint. HOTS
         * is also the one mode that opts into showFinishOnLast, so its last question ends the
         * session instead of quietly running out of questions.
         *
         * The Rise ASSESSMENT falling here is worth knowing: a test arguably wants one attempt, as
         * the quiz has, but it has always had two and changing that would change how Rise is marked.
         * That is why isSingleAttempt is opt-in - the quiz asked for it, nothing else was touched.
         *
         * Every branch disables the question once the answer is final; that is what the explanation
         * box keys off, and in a quiz it is the student's only signal that the answer is in.
         */
        if (challengeContext.isChallenge) {
            // Daily challenge: one attempt, and the last question of the set ends it.
            setDisabled(true);
            if (!answerObj.isCorrectAns)
                setShowExplanation(true);
            if (!answerObj.isCorrectAns && challengeContext.attempted === challengeContext.total - 1)
                setShowFinish(true);
        }
        else if (state.isSafeZone) {
            // Safe zone: one attempt, and a mistake on the last question ends the session. Getting
            // it right simply closes the question - there is nothing to explain.
            setDisabled(true);
            if (!answerObj.isCorrectAns) {
                setShowExplanation(true);
                if (state.isLastQuestion)
                    setShowFinish(true);
            }
        }
        else if (state.isSingleAttempt) {
            /* ONE attempt: the answer is final, so the question closes - the student's signal that it
               is in, and that moving on is their job now.
       
               NOT where the answer is saved by hand. There the question stays open on purpose: a
               student who saves and then thinks better of it picks another option, or types another
               number, and presses Save again - which is the whole point of a Save button, and the
               state the question would be in if they had never pressed it. Closing it here left them
               with nothing to do but Clear. Save going grey is the signal instead: lit means there is
               something not saved yet. */
            if (!defersAnswer)
                setDisabled(true);
            // And the explanation, if this mode reveals one. The quiz does not (showAnswer: false),
            // so nothing opens there; a one-attempt mode that leaves showAnswer unset gets the
            // worked answer at once, since there is no second try to save it for.
            if (!revealsNothing && !answerObj.isCorrectAns)
                setShowExplanation(true);
        }
        else {
            // Adaptive practice: TWO attempts. A first wrong answer gets a hint and another go; a
            // second gets the worked explanation and closes the question. Getting it right closes it
            // at once.
            // A mode that reveals nothing keeps both shut, however they answered. Nothing else here
            // changes: the question still closes after the second attempt, and still scores.
            let hintStatus = (!revealsNothing && count === 1 && !answerObj.isCorrectAns) ? true : false;
            let expStatus = (!revealsNothing && count === 2 && !answerObj.isCorrectAns) ? true : false;
            setShowHint(hintStatus);
            setShowExplanation(expStatus);
            if (count === 2 || answerObj.isCorrectAns)
                setDisabled(true);
            // Opt-in, so adaptive practice is untouched: a mode whose last question ENDS the
            // session (HOTS) labels that explanation's button Finish and ends on the click,
            // rather than Next quietly running out of questions. Same shape the daily challenge
            // and Safe Zone already use above.
            if (expStatus && state.isLastQuestion && state.showFinishOnLast)
                setShowFinish(true);
        }


        state.saveQuestionResponse(answerObj, count);

        // As it always did - except where the answer is saved by hand, which keeps it on screen: that
        // is the student's own work, and the whole point of a Save they can press again.
        if (!defersAnswer)
            clearInputBoxes();
    }

    /**
     * OFFER THIS QUESTION'S ANSWER TO THE FRAME, which is what hands it in.
     *
     * Only the part that drew the answer knows how to read it - a choice is the option last marked,
     * everything else is whatever the engine finds in the question when Done is pressed - and only
     * the frame knows when it counts, which is when the student says so. So the reading is registered
     * here and called from there.
     *
     * A quiz alone: every other mode takes the answer on the spot. Registered on every render rather
     * than once, so the handler the frame holds is always this render's - a choice marked a moment
     * ago has to be the one handed in.
     */
    useEffect(() => {
        if (!defersAnswer || isReadOnly || isReviewing) return;
        if (extraProps.type === 'doneButton') {
            state.registerCommit?.(() => { onClick(id, clickedVal, actualVal, extraProps) });
            return () => state.registerCommit?.(null);
        }
        if (extraProps.type === 'choices') {
            state.registerCommit?.(() => {
                const picked = extraProps.children?.find((child: any) => child.id === clickedChoiceRef.current);
                // Nothing marked is nothing to hand in: the question stays unanswered, which is what
                // Skip does deliberately.
                if (picked)
                    onClick(picked.id, picked.clickedVal, picked.actualVal, picked.extraProps);
            });
            return () => state.registerCommit?.(null);
        }
    });

    return (<>
        {/* Done Button - left out entirely when the question is only being SHOWN.
            `onClick` already returns on a read-only question, so the button could never hand an
            answer in; drawn anyway it invites a press that does nothing. It also has nowhere to be in
            a question on its way to a downloaded sheet, or in one still loading behind a frame. */}
        {(extraProps.type === "doneButton" && !isReadOnly) && (
            /* HIDDEN IN A QUIZ, not removed. On a long question it sat below the fold while Prev and
               Next stayed in view, so a student answered, pressed Next and lost the answer. The quiz
               hands questions in from the footer now, and this button is what the footer presses -
               the engine reads the answer through it, so it has to still be here. */
            <button id={id} style={style} onClick={() => { onClick(id, clickedVal, actualVal, extraProps) }}
                disabled={disabled} className={`${className || ''}${defersAnswer ? ' hidden-done-btn' : ''}`}>{children}</button>
        )}
        {/* Choices */}
        {(extraProps.type === "choices") && (
            <div id={id} className={className} style={style}>
                {extraProps.children.map((child: any, ii: number) => {
                    return (
                        <button key={ii} id={child.id} className={child.class}
                            style={{
                                ...stylesMap(child.styles),
                                ...(clickedChoice === child.id ?
                                    {
                                        border: '3px solid orange',
                                        borderRadius: '10px',
                                        boxShadow: '0 0 15px 5px orange'
                                    } : {})
                            }}
                            disabled={disabled} onClick={() => {
                                markChoice(child.id);
                                /* A QUIZ OR AN ASSESSMENT only marks it. The answer is handed in by the
                                   Save button under the frame, so a student can change their mind - tap
                                   another option - while the question is still open. Everywhere else a
                                   tap is still the answer, as it has always been: practice explains it
                                   on the spot, and there is nothing to wait for. */
                                if (!defersAnswer)
                                    setTimeout(() => { onClick(child.id, child.clickedVal, child.actualVal, child.extraProps) }, 100);
                            }}>
                            {child.children.map((val: any) => {
                                let objRendered;
                                objRendered = renderer(val);
                                return objRendered;
                            })}
                        </button>
                    )
                })}

            </div>

        )}

        {/* The explanation box - see showExplanationBox for the three reasons it opens. */}
        {showExplanationBox && (
            <div id="explanationBox" className="info-box">
                <div id="explanationContent" className="d-flex info-content explanation">
                    {/* Fold the explanation away, or bring it back. One arrow or the other, always. */}
                    {(showExplanation) && (
                        <button id="downButton" onClick={() => { setShowExplanation(false) }}><img className="exp-down-btn" src={exp.extraProps.downBtnUrl}></img></button>
                    )}
                    {(!showExplanation) && (
                        <button id="upButton" onClick={() => { setShowExplanation(true) }} className="exp-up-btn"><img src={exp.extraProps.upBtnUrl}></img></button>
                    )}
                    {/* Moving on from here, where the host asked for that button. The quiz does not:
                        it walks the questions with Prev / Next under the frame instead. Finish
                        replaces Next on a question that ENDS the session - see showFinish. */}
                    {(state.isShowExpNext) && (
                        <button id="nextButton" onClick={showFinish ? state.onFinish : state.loadContent} className="next-question-btn buttonStyle1">{showFinish ? "Finish" : "Next"}</button>
                    )}
                    {/* The verdict, in a review only: right, wrong, or never attempted.

                        THE SAME THREE COLOURS AS THE NUMBER'S RING ABOVE, because they say the same
                        three things about the same question. They are not written out here: --mark-
                        correct, --mark-wrong and --mark-blank live in playFrame.css, so the ring,
                        this verdict, the tallies and the buttons under the frame cannot drift apart
                        again - which is how this box came to say in grey what the number beside it
                        said in amber. */}
                    {(isReviewing) && (
                        <div className='answer-review'>
                            {(!reviewMode.isAttempted) &&
                                (<>
                                    <FAExclamationCircle color='var(--mark-blank)'></FAExclamationCircle>
                                    <span style={{ color: 'var(--mark-blank)' }}> Not Attempted</span>
                                </>)}

                            {(reviewMode.isAttempted) && (<>
                                {reviewMode.isCorrect ?
                                    <FATickMark color='var(--mark-correct)'></FATickMark> :
                                    <FAIncorrect color='var(--mark-wrong)'></FAIncorrect>}
                                <span style={{ color: reviewMode.isCorrect ? 'var(--mark-correct)' : 'var(--mark-wrong)' }}>{reviewMode.isCorrect ? ' Correct' : ' Incorrect'}</span>
                            </>)}
                        </div>)}
                    {/* The explanation itself - only while unfolded, so the box can be open and quiet. */}
                    {(showExplanation) && (<>
                        <Player id={"explanationBoy"} className={"info-boy"} autoplay loop speed={1} src={exp.extraProps.expBoyUrl}></Player>
                        {/* <img id="explanationBoy" className="info-boy" src={exp.extraProps.expBoyUrl}></img> */}
                        <div id="explanationText"
                            className="info-text d-flex align-items-start justify-content-start w-100">
                            {
                                exp.children.map((val: any) => {
                                    let objRendered;
                                    // console.log(state.outputJSON.explanation);
                                    objRendered = renderer(val);
                                    return objRendered;
                                })
                            }
                        </div></>)}
                </div>
            </div >
        )}

        {/* The hint - practice only, after a FIRST wrong answer, and never anywhere that gives one
            attempt. Nothing else opens it, which is why this needs no mode of its own: the only
            place setShowHint(true) is reached is the practice branch of onClick. */}
        {
            (showHint) && (
                <div id="hintBox" className="info-box">
                    <div id="hintContent" className="d-flex info-content hint ">
                        <button id="closeHintButton" onClick={() => { setShowHint(false) }}><img className="close-hint" src={hint.extraProps.hintClose}></img></button>
                        <Player id={"hintBoy"} className={"info-boy"} autoplay loop speed={1} src={hint.extraProps.hintBoyUrl}></Player>
                        {/* <img id="hintBoy" className="info-boy" src={hint.extraProps.hintBoyUrl}></img> */}
                        <div id="hintText" className="info-text d-flex align-items-start justify-content-start w-100">
                            {
                                hint.children.map((val: any) => {
                                    let objRendered;
                                    objRendered = renderer(val);
                                    return objRendered;
                                })
                            }
                        </div>
                    </div>
                </div>
            )
        }
    </>)

}